/**
 * @file Constants — apiUrls.
 * API URL Constants
 * Centralized location for all API endpoints
 */

export const API_URLS = {
  // Profile
  PROFILE_ME: "/profile/me",

  // Forms
  FORMS_STAGE: (stageCode: string) => `/forms/stage/${stageCode}`,

  // Submissions
  SUBMISSIONS: {
    CREATE: "/submissions/create",
    MINE: "/submissions/mine",
    /** Contributor: FLAGGED_ASSIGNED_TO_CONTRIBUTOR | FLAGGED_BY_EXPERT only */
    FLAGGED: "/submissions/flagged",
    /** Completed list; optional ?status=<ReviewStatus> (v2 enum only) */
    COMPLETED_REVIEW_STATUS: "/submissions/completed/review-status",
    BY_ID: (id: string) => `/submissions/${id}`,
    DELETE: (id: string) => `/submissions/${id}`,
    CONTRIBUTOR: (id: string) => `/submissions/${id}/contributor`,
    PUBLICATION: (id: string) => `/submissions/${id}/publication`,
    BACKGROUND: (id: string) => `/submissions/${id}/background`,
    A1: (id: string) => `/submissions/${id}/a1`,
    A2: (id: string) => `/submissions/${id}/a2`,
    A3: (id: string) => `/submissions/${id}/a3`,
    A1_A3: (id: string) => `/submissions/${id}/a1-a3`,
    STAGE: (id: string) => `/submissions/${id}/stage`,
  },

  // Dropdowns
  DROPDOWNS: "/dropdowns",

  /** Extraction processes for a finished material (Background materialId). */
  EXTRACTION_PROCESSES: "/extraction-processes",

  /** Raw material quality attribute catalog for a finished material (public). */
  RAW_MATERIAL_QUALITY_ATTRIBUTES: "/raw-material-quality-attributes",

  /** Plant capacity bands for a finished material (public). */
  PLANT_CAPACITIES: "/plant-capacities",

  /** Reference fuel densities (kg/L, kg/m³) for A1 fuel mix auto-fill. */
  FUEL_DENSITIES: "/fuel-densities",

  // Materials
  MATERIALS: {
    LIST: "/materials",
    BY_ID: (id: number | string) => `/materials/${id}`,
  },

  /** Catalog raw materials for a finished material (`raw_materials`, not background junction rows). */
  RAW_MATERIALS: {
    BY_MATERIAL: (materialId: number | string) =>
      `/raw-materials/material/${materialId}`,
  },

  // Sub Materials
  SUB_MATERIALS: {
    BY_ID: (id: number | string) => `/sub-materials/${id}`,
    BY_MATERIAL: (materialId: number | string) =>
      `/sub-materials/material/${materialId}`,
  },

  // Units
  UNITS: {
    BY_PROPERTY: (propertyId: number | string) => `/units/property/${propertyId}`,
  },

  // Forms Admin
  FORMS: {
    SECTIONS: "/forms/sections",
    SUBSECTIONS: "/forms/subsections",
    FIELDS: "/forms/fields",
    FIELD_BY_ID: (id: number) => `/forms/fields/${id}`,
  },

  /**
   * Dynamic forms CMS — `/api/forms/admin/...` (authMiddleware + requireAdmin).
   * Equivalent: `/api/admin/forms/...` (global admin role).
   */
  FORMS_ADMIN: {
    ALL_STAGES: "/forms/admin",
    FIELD_TYPES: "/forms/admin/field-types",
    TEXT_INPUT_TYPES: "/forms/admin/text-input-types",
    FIELD_POST: "/forms/admin/field",
    FIELD_BY_ID: (fieldId: number | string) => `/forms/admin/field/${fieldId}`,
    FIELDS_REORDER: "/forms/admin/fields/reorder",
  },

  // User Dashboard
  USER_DASHBOARD: {
    MATERIAL_LIBRARY: "/userdashboard/material-library",
    MATERIAL_ANALYSIS: "/userdashboard/material-analysis",
    MATERIAL_PROPERTY_CONFIG: "/userdashboard/material-property-config",
  },

  // Landing/Public
  LANDING: {
    STATS: "/landing/stats",
  },

  // Public static pages (CMS-managed)
  PAGES: {
    BY_SLUG: (slug: string) => `/pages/${slug}`,
  },

  /** Cite-this-tool sidebar menu and modal (GET all dashboards; PUT admin) */
  TOOL_CITATION: "/tool-citation",

  // Admin: manage public/static pages
  ADMIN: {
    PAGES: {
      LIST: "/admin/pages",
      BY_SLUG: (slug: string) => `/admin/pages/${slug}`,
    },
    DATASETS: "/admin/datasets",
    DATASETS_DOWNLOAD: (materialId: number | string) =>
      `/admin/datasets/download?materialId=${materialId}`,
    SUBMISSIONS: {
      FLAG_ADMIN: (id: string) => `/admin/submissions/${id}/flag/admin`,
      FLAG_CONTRIBUTOR: (id: string) => `/admin/submissions/${id}/flag/contributor`,
      FLAG_EXPERT: (id: string) => `/admin/submissions/${id}/flag/expert`,
      APPROVE: (id: string) => `/admin/submissions/${id}/approve`,
      ASSIGN: (id: string) => `/admin/submissions/${id}/assign`,
    },
    /** Admin CRUD; public list (active only): GET /characterization-factors */
    CHARACTERIZATION_FACTORS: {
      LIST: "/admin/characterization-factors",
      BY_ID: (id: number | string) => `/admin/characterization-factors/${id}`,
    },
    /** Admin CRUD; public list (active only): GET /calorific-values */
    CALORIFIC_VALUES: {
      LIST: "/admin/calorific-values",
      BY_ID: (id: number | string) => `/admin/calorific-values/${id}`,
    },
    /** Admin CRUD; public list (active only): GET /fuel-densities */
    FUEL_DENSITIES: {
      LIST: "/admin/fuel-densities",
      BY_ID: (id: number | string) => `/admin/fuel-densities/${id}`,
    },
    /** Admin CRUD; public list: GET /manufacturing-processes?materialId= */
    MANUFACTURING_PROCESSES: {
      LIST: "/admin/manufacturing-processes",
      BY_ID: (id: number | string) => `/admin/manufacturing-processes/${id}`,
    },
    /** Admin CRUD; public list: GET /plant-capacities?materialId= */
    PLANT_CAPACITIES: {
      LIST: "/admin/plant-capacities",
      BY_ID: (id: number | string) => `/admin/plant-capacities/${id}`,
    },
    /** Admin CRUD; public list (active only): GET /vehicle-category-emission-factors */
    VEHICLE_CATEGORY_EMISSION_FACTORS: {
      LIST: "/admin/vehicle-category-emission-factors",
      BY_ID: (id: number | string) =>
        `/admin/vehicle-category-emission-factors/${id}`,
    },
  },

  /** Public: active characterization factors (no auth), ordered by sortOrder */
  CHARACTERIZATION_FACTORS: "/characterization-factors",

  /** Public: active calorific values (no auth), ordered by sortOrder */
  CALORIFIC_VALUES: "/calorific-values",

  /** Public: active manufacturing process steps for a material (`?materialId=`). */
  MANUFACTURING_PROCESSES: "/manufacturing-processes",

  /** Public: A2 vehicle category emission factors (active only); sortOrder, then id. */
  VEHICLE_CATEGORY_EMISSION_FACTORS: "/vehicle-category-emission-factors",

  // Expert
  EXPERT: {
    DATASETS: "/expert/datasets",
    /** Strict expert queue (assigned work) */
    SUBMISSIONS: "/expert/submissions",
    SUBMISSIONS_BY_ASSIGNED_MATERIAL: "/expert/submissions/by-assigned-material",
    REVIEW: (submissionId: string) => `/expert/review/${submissionId}`,
  },

  // Technological specificities
  TECHNOLOGICAL_SPECIFICITIES: {
    BY_MATERIAL: (materialId: number | string) =>
      `/technological-specificities/material/${materialId}`,
  },

  // Manufacturer specificities
  MANUFACTURER_SPECIFICITIES: {
    BY_MATERIAL: (materialId: number | string) =>
      `/manufacturer-specificities/material/${materialId}`,
  },
} as const;

